import React from "react";
import { Container, Grid, Typography, Link, Box, Divider, IconButton, Stack } from "@mui/material";
import Email from "./assets/Email.png";
import Phone from "./assets/Phone.png";
import Location from "./assets/Location.png";
import Logo from "./assets/Logo.png";
import LinkedIn from "./assets/linkedin.png";
import Twitter from "./assets/twitter.webp";
import YouTube from "./assets/youtube.webp";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const headingStyle = {
    fontWeight: 700,
    marginBottom: 2,
    fontSize: { xs: 14, sm: 15, md: 15 },
    color: "#F8FAFC",
    letterSpacing: '0.5px',
  };

  const linkStyle = {
    display: "block",
    marginBottom: 1.5,
    color: "rgba(255,255,255,0.6)",
    textDecoration: "none",
    fontSize: { xs: 13, md: 14 },
    transition: "all 0.3s ease",
    "&:hover": {
      color: "#E2684A",
      textShadow: "0 0 10px rgba(226, 104, 74, 0.5)",
      transform: "translateX(3px)",
    },
  };

  const socialIconStyle = {
    width: "28px",
    height: "28px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    filter: "grayscale(100%) brightness(0.8)",
  };

  return (
    <Box
      sx={{
        // Cosmic dark background matching theme
        backgroundColor: "#030014",
        color: "rgba(255,255,255,0.7)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* GRADIENT TOP BORDER */}
      <Box
        sx={{
          height: "2px",
          background: "linear-gradient(90deg, #E2684A 0%, #7C3AED 50%, #22D3EE 100%)",
          boxShadow: "0 0 20px rgba(226, 104, 74, 0.5)",
        }}
      />

      {/* Subtle background glow */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: "10%",
          width: "300px",
          height: "300px",
          background: "radial-gradient(circle, rgba(124, 58, 237, 0.08) 0%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          right: "10%",
          width: "300px",
          height: "300px",
          background: "radial-gradient(circle, rgba(226, 104, 74, 0.08) 0%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 }, position: "relative", zIndex: 1 }}>
        <Grid container spacing={4}>
          {/* Logo & Tagline Column */}
          <Grid item xs={12} sm={12} md={3}>
            <Box sx={{ mb: 3 }}>
              <img src={Logo} alt="Beanbag AI" style={{ height: "40px" }} />
            </Box>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: "rgba(255,255,255,0.7)",
                fontWeight: 400,
                fontSize: { xs: 14, md: 15 },
                lineHeight: 1.8,
              }}
            >
              Architecting the Intelligent Enterprise — from strategy to deployment.
            </Typography>

            {/* Social Icons with hover glow */}
            <Stack direction="row" spacing={1}>
              <IconButton
                href="https://linkedin.com/company/beanbag-ai"
                target="_blank"
                sx={{
                  p: 1,
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 2,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "#E2684A",
                    boxShadow: "0 0 15px rgba(226, 104, 74, 0.4)",
                    "& img": { filter: "grayscale(0%) brightness(1)" }
                  }
                }}
              >
                <img src={LinkedIn} alt="LinkedIn" style={socialIconStyle} />
              </IconButton>
              <IconButton
                href="https://x.com/BeanbagAi/"
                target="_blank"
                sx={{
                  p: 1,
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 2,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "#7C3AED",
                    boxShadow: "0 0 15px rgba(124, 58, 237, 0.4)",
                    "& img": { filter: "grayscale(0%) brightness(1)" }
                  }
                }}
              >
                <img src={Twitter} alt="Twitter" style={{ ...socialIconStyle, width: "32px", height: "24px" }} />
              </IconButton>
              <IconButton
                href="https://www.youtube.com/channel/UCIJnFHNEHpjPhRGn_jI8gTA"
                target="_blank"
                sx={{
                  p: 1,
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 2,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "#22D3EE",
                    boxShadow: "0 0 15px rgba(34, 211, 238, 0.4)",
                    "& img": { filter: "grayscale(0%) brightness(1)" }
                  }
                }}
              >
                <img src={YouTube} alt="YouTube" style={{ ...socialIconStyle, width: "32px", height: "32px" }} />
              </IconButton>
            </Stack>
          </Grid>

          {/* Solutions Column */}
          <Grid item xs={6} sm={6} md={2.5}>
            <Typography variant="h6" sx={headingStyle}>Solutions</Typography>
            <Link href="/solutions/strategic-consulting" sx={linkStyle}>Strategic AI Consulting</Link>
            <Link href="/solutions/genai-engineering" sx={linkStyle}>GenAI & LLM Engineering</Link>
            <Link href="/solutions/intelligent-agents" sx={linkStyle}>Intelligent Agents</Link>
            <Link href="/solutions/data-engineering" sx={linkStyle}>Data Engineering</Link>
          </Grid>

          {/* Industries Column */}
          <Grid item xs={6} sm={6} md={2}>
            <Typography variant="h6" sx={headingStyle}>Industries</Typography>
            <Link href="/#industries" sx={linkStyle}>Healthcare</Link>
            <Link href="/#industries" sx={linkStyle}>Banking & FinTech</Link>
            <Link href="/#industries" sx={linkStyle}>Retail & E-Commerce</Link>
            <Link href="/#industries" sx={linkStyle}>Manufacturing</Link>
          </Grid>

          {/* Resources Column */}
          <Grid item xs={6} sm={6} md={2}>
            <Typography variant="h6" sx={headingStyle}>Resources</Typography>
            <Link href="/blog" sx={linkStyle}>Blog</Link>
            <Link href="/case-studies" sx={linkStyle}>Case Studies</Link>
            <Link href="/templates" sx={linkStyle}>Templates</Link>
            <Link href="/tools" sx={linkStyle}>Tools</Link>
          </Grid>

          {/* Contact Column */}
          <Grid item xs={6} sm={6} md={2.5}>
            <Typography variant="h6" sx={headingStyle}>Get In Touch</Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <img src={Email} alt="email" style={{ width: "18px", opacity: 0.7 }} />
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", fontSize: 14 }}>
                  team@beanbag.ai
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <img src={Phone} alt="phone" style={{ width: "18px", opacity: 0.7 }} />
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", fontSize: 14 }}>
                  +91 998 584 7341
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <img src={Location} alt="location" style={{ width: "18px", opacity: 0.7, marginTop: "2px" }} />
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", fontSize: 14, lineHeight: 1.6 }}>
                  8 The Green, Suite #15213<br />Dover, DE, USA 19901
                </Typography>
              </Stack>
            </Stack>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={headingStyle}>Company</Typography>
              <Link href="/privacypolicy" sx={linkStyle}>Privacy Policy</Link>
              <Link href="/termsofuse" sx={linkStyle}>Terms of Service</Link>
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <Divider sx={{ mt: 6, mb: 3, borderColor: "rgba(255,255,255,0.08)" }} />
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.4)", fontSize: 13 }}>
            © {currentYear} Remote Community Private Limited — All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;